/** Paramètres super-admin (section 2) : rubriques Instance · Emails · Journaux · Export · Import.
 * Colonne de rubriques à gauche (sticky), contenu à droite. Point sur les rubriques modifiées,
 * enregistrement par rubrique, lien « Annuler les modifications ». */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../../services/api';
import { Button, SegmentedControl } from '../../../components/ui/kit';
import { useAutoSave, AutoSaveStatus } from '../../../hooks/useAutoSave';
import { PanelCard, Field } from '../FullPanel';
import { APP_VERSION } from '../constants';
import { AppellationIndexImport } from '../../parametres/rubrics/Importer';
import IASettings from '../../parametres/rubrics/IASettings';
import { adminApi as _adminApi } from '../../../services/api';

// Adaptateur super-admin pour le composant IASettings partagé (config IA globale).
const ADMIN_AI_API = {
  load: () => _adminApi.aiConfig(),
  save: (v) => _adminApi.updateAiConfig(v),
  saveKey: (p, k) => _adminApi.saveAiKey(p, k),
  deleteKey: (p) => _adminApi.deleteAiKey(p),
  showPlaces: false,
  title: 'IA · OCR et traduction (défaut plateforme)',
};

const RUBRICS = [
  { key: 'instance', label: 'Instance' },
  { key: 'emails', label: 'Emails' },
  { key: 'logs', label: 'Journaux' },
  { key: 'export', label: 'Export' },
  { key: 'import', label: 'Import' },
  { key: 'ia', label: 'IA · OCR' },
  { key: 'superadmin', label: 'Super Admin' },
];

function SaveBar({ dirty, onSave, onCancel, saveLabel = 'Enregistrer' }) {
  return (
    <div className="flex items-center gap-3 mt-4">
      <Button variant="primary" disabled={!dirty} onClick={onSave} data-testid="rubric-save">{saveLabel}</Button>
      {dirty && <><button className="vd-link text-sm" onClick={onCancel}>Annuler les modifications</button>
        <span className="text-xs text-n600">Modifications non enregistrées</span></>}
    </div>
  );
}

// ---------------- Instance ----------------
function InstanceRubric({ settings, instance, setDirty }) {
  const [form, setForm] = useState(settings.instance);
  const status = useAutoSave(form, (v) => adminApi.putSettings('instance', v));
  useEffect(() => setDirty(false), [setDirty]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><p className="kicker">Instance</p><AutoSaveStatus status={status} /></div>
      <PanelCard title="Instance" testId="rubric-instance">
        <Field label="Nom de la plateforme"><input className="vd-input" value={form.platform_name} onChange={(e) => set('platform_name', e.target.value)} /></Field>
        <Field label="URL publique"><input className="vd-input" value={form.public_url} onChange={(e) => set('public_url', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Fuseau horaire"><input className="vd-input" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} /></Field>
          <Field label="Langue par défaut"><input className="vd-input" value={form.default_language} onChange={(e) => set('default_language', e.target.value)} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.maintenance} onChange={(e) => set('maintenance', e.target.checked)} data-testid="maintenance-toggle" /> Mode maintenance (seuls les super-admins peuvent se connecter)</label>
        {form.maintenance && <Field label="Message affiché aux utilisateurs"><textarea className="vd-input h-20 py-2" value={form.maintenance_message} onChange={(e) => set('maintenance_message', e.target.value)} /></Field>}
      </PanelCard>
      <PanelCard title="Informations (lecture seule)" className="mt-4">
        <p className="text-sm mb-1">Identifiant d'instance : <span className="adm-mono text-xs">{instance?.instance_id}</span>
          <button className="vd-link text-xs ml-2" onClick={() => { navigator.clipboard?.writeText(instance?.instance_id || ''); toast.success('Copié'); }}>Copier</button></p>
        <p className="text-sm mb-1">Version : <span className="adm-mono">{APP_VERSION}</span></p>
        <p className="text-sm">Hébergement : {instance?.hosting || '—'}</p>
      </PanelCard>
    </div>
  );
}

// ---------------- Emails ----------------
const SMTP_STATUS = { connected: ['connecté', 'adm-badge-active'], untested: ['à tester', 'adm-badge-warn'], failed: ['échec', 'adm-badge-error'] };
function EmailsRubric({ smtp, emails, reload, setDirty }) {
  const base = { host: smtp.host || '', port: smtp.port || 587, security: smtp.security || 'STARTTLS',
    username: smtp.username || '', password: '', sender_name: smtp.sender_name || '',
    from_address: smtp.from_address || '', reply_to: smtp.reply_to || '', tenant_name_as_sender: !!smtp.tenant_name_as_sender };
  const [form, setForm] = useState(base);
  const [testTo, setTestTo] = useState(() => localStorage.getItem('vd_smtp_test_to') || '');
  const [testing, setTesting] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(base);
  useEffect(() => setDirty(dirty), [dirty, setDirty]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [st, cls] = SMTP_STATUS[smtp.status] || SMTP_STATUS.untested;
  const save = () => adminApi.putSmtp(form).then(() => { toast.success('SMTP enregistré (à tester)'); reload(); }).catch((e) => toast.error(e.message));
  const test = async () => {
    setTesting(true);
    try {
      if (testTo) localStorage.setItem('vd_smtp_test_to', testTo);
      const r = await adminApi.testSmtp(testTo ? { to: testTo } : {});
      r.status === 'connected' ? toast.success(r.message || 'Connecté') : toast.error(r.message || 'Échec');
      reload();
    } catch (e) { toast.error(e.message); } finally { setTesting(false); }
  };
  return (
    <div>
      <p className="text-sm text-n600 mb-4">Ce serveur SMTP sert à tous les envois de la plateforme : invitations, accès, réinitialisations de mot de passe, exports, et emails envoyés par les établissements.</p>
      <PanelCard title="Serveur SMTP" testId="rubric-smtp">
        <div className="flex items-center gap-2 mb-3"><span className={`adm-badge ${cls}`} data-testid="smtp-status">{st}</span></div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Hôte"><input className="vd-input" value={form.host} onChange={(e) => set('host', e.target.value)} data-testid="smtp-host" /></Field>
          <Field label="Port"><input className="vd-input" type="number" value={form.port} onChange={(e) => set('port', Number(e.target.value))} /></Field>
        </div>
        <Field label="Sécurité">
          <SegmentedControl value={form.security} onChange={(v) => set('security', v)}
            options={[{ value: 'STARTTLS', label: 'STARTTLS' }, { value: 'SSL_TLS', label: 'SSL/TLS' }, { value: 'NONE', label: 'Aucune' }]} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Utilisateur"><input className="vd-input" value={form.username} onChange={(e) => set('username', e.target.value)} /></Field>
          <Field label={`Mot de passe ${smtp.password_set ? '(enregistré — laisser vide pour conserver)' : ''}`}><input className="vd-input" type="password" autoComplete="new-password" value={form.password} onChange={(e) => set('password', e.target.value)} data-testid="smtp-password" /></Field>
        </div>
      </PanelCard>
      <PanelCard title="Expéditeur" className="mt-4">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Nom affiché"><input className="vd-input" value={form.sender_name} onChange={(e) => set('sender_name', e.target.value)} /></Field>
          <Field label="Adresse d'envoi"><input className="vd-input" value={form.from_address} onChange={(e) => set('from_address', e.target.value)} /></Field>
        </div>
        <Field label="Répondre à"><input className="vd-input" value={form.reply_to} onChange={(e) => set('reply_to', e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.tenant_name_as_sender} onChange={(e) => set('tenant_name_as_sender', e.target.checked)} /> Pour les emails d'un établissement, afficher son nom comme expéditeur</label>
        <SaveBar dirty={dirty} onSave={save} onCancel={() => setForm(base)} />
      </PanelCard>
      <PanelCard title="Test d'envoi" className="mt-4">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Destinataire"><input className="vd-input" style={{ width: 'auto', minWidth: 220 }} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email@exemple.fr" data-testid="smtp-test-to" /></Field>
          <Button onClick={test} disabled={testing} data-testid="smtp-test">{testing ? 'Test en cours…' : 'Envoyer un email de test'}</Button>
        </div>
        <p className="text-xs text-n600 mt-2">{emails.h24} emails envoyés en 24 h · {emails.d30} sur 30 jours</p>
      </PanelCard>
    </div>
  );
}

// ---------------- Journaux ----------------
function LogsRubric({ settings, reload, setDirty }) {
  const init = { retention_days: settings.logs.retention_days, level: settings.logs.level,
    alert_enabled: settings.logs.alert_enabled, alert_email: settings.logs.alert_email };
  const [form, setForm] = useState(init);
  const status = useAutoSave(form, (v) => adminApi.putSettings('logs', v));
  useEffect(() => setDirty(false), [setDirty]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const purge = () => adminApi.purgeOlder(form.retention_days).then((r) => { toast.success(`${r.purged} entrées purgées`); reload(); }).catch((e) => toast.error(e.message));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><p className="kicker">Journaux</p><AutoSaveStatus status={status} /></div>
      <PanelCard title="Journaux" testId="rubric-logs">
        <Field label="Conservation (jours)"><input className="vd-input" type="number" min="1" value={form.retention_days} onChange={(e) => set('retention_days', Number(e.target.value))} data-testid="logs-retention" /></Field>
        <Field label="Niveau de détail">
          <SegmentedControl value={form.level} onChange={(v) => set('level', v)}
            options={[{ value: 'essential', label: 'Essentiel' }, { value: 'detailed', label: 'Détaillé' }]} />
          <span className="block text-xs text-n500 mt-1">Essentiel : connexions, actions admin, emails, erreurs. Détaillé : ajoute mouvements de stock et consultations de carte.</span>
        </Field>
        <div className="flex items-center gap-3 my-3">
          <span className="text-sm text-n600">{settings.logs.older_count ?? 0} entrées de plus de {form.retention_days} jours</span>
          <Button onClick={purge} data-testid="logs-purge-older">Purger maintenant</Button>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.alert_enabled} onChange={(e) => set('alert_enabled', e.target.checked)} /> M'avertir par email à chaque erreur serveur</label>
        {form.alert_enabled && <Field label="Destinataire des alertes"><input className="vd-input" value={form.alert_email} onChange={(e) => set('alert_email', e.target.value)} /></Field>}
      </PanelCard>
    </div>
  );
}

// ---------------- Export ----------------
const EXPORTS = [
  ['config', 'Configuration complète', 'Instance, emails (sans mot de passe), journaux, export, index des appellations. Réimportable.', 'JSON'],
  ['journal', 'Journal', 'Toutes les entrées conservées, de la plus récente à la plus ancienne.', 'CSV'],
  ['tenants', 'Établissements', 'Type, slug, état, coordonnées, dernière activité.', 'CSV'],
  ['users', 'Utilisateurs', 'Établissement, rôle, état, dernière connexion. Aucun mot de passe.', 'CSV'],
];
const EXT = { config: 'json', journal: 'csv', tenants: 'csv', users: 'csv' };
const NAMES = { config: 'cavorama-config', journal: 'cavorama-journal', tenants: 'cavorama-etablissements', users: 'cavorama-utilisateurs' };
function ExportRubric({ settings, reload, setDirty }) {
  const init = { enabled: settings.export.enabled, day: settings.export.day, hour: settings.export.hour, send_to: settings.export.send_to || '' };
  const [form, setForm] = useState(init);
  const status = useAutoSave(form, (v) => adminApi.putSettings('export', v));
  useEffect(() => setDirty(false), [setDirty]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const run = () => adminApi.runNow().then((r) => { toast.success(`Export lancé : ${r.result}`); reload(); }).catch((e) => toast.error(e.message));
  const doExport = (kind) => adminApi.exportFile(kind, `${NAMES[kind]}-${new Date().toISOString().slice(0, 10)}.${EXT[kind]}`)
    .then(() => toast.success('Fichier téléchargé')).catch((e) => toast.error(e.message));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><p className="kicker">Export</p><AutoSaveStatus status={status} /></div>
      <PanelCard title="Export manuel" testId="rubric-export">
        <div className="space-y-2">
          {EXPORTS.map(([kind, title, desc, fmt]) => (
            <div key={kind} className="flex items-center gap-3 py-2 border-b border-hair/50 last:border-0">
              <div className="min-w-0 flex-1"><p className="text-sm font-medium">{title} <span className="adm-badge ml-1">{fmt}</span></p><p className="text-xs text-n600">{desc}</p></div>
              <Button onClick={() => doExport(kind)} data-testid={`export-${kind}`}>Exporter</Button>
            </div>
          ))}
        </div>
      </PanelCard>
      <PanelCard title="Export mensuel automatique" className="mt-4">
        <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Activé</label>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Jour (1–28)"><input className="vd-input" type="number" min="1" max="28" value={form.day} onChange={(e) => set('day', Number(e.target.value))} /></Field>
          <Field label="Heure (UTC)"><input className="vd-input" type="number" min="0" max="23" value={form.hour} onChange={(e) => set('hour', Number(e.target.value))} /></Field>
          <Field label="Envoyer à"><input className="vd-input" value={form.send_to} onChange={(e) => set('send_to', e.target.value)} /></Field>
        </div>
        <p className="text-sm text-n700 mt-1">Prochain export : le {form.day} de chaque mois à {String(form.hour).padStart(2, '0')}:00 UTC{form.send_to ? `, envoyé à ${form.send_to}` : ''}.</p>
        <p className="text-xs text-n600 mt-1">Dernière exécution : {settings.export.last_run ? `${settings.export.last_run.slice(0, 16).replace('T', ' ')} (${settings.export.last_result})` : '—'}</p>
        <div className="flex items-center gap-3 mt-2"><Button onClick={run} data-testid="export-run-now">Lancer maintenant</Button></div>
      </PanelCard>
    </div>
  );
}

// ---------------- Import ----------------
const CONFIG_COLS = [
  ['version', 'obligatoire', 'Version du fichier (ex. 0.2)'],
  ['instance', 'obligatoire', 'Nom, URL, fuseau horaire, langue, maintenance'],
  ['smtp', 'facultatif', "Serveur d'envoi et expéditeur, sans mot de passe"],
  ['logs', 'facultatif', 'Conservation, niveau, alertes'],
  ['export', 'facultatif', 'Export mensuel automatique'],
  ['appellations', 'facultatif', 'Index complet'],
];
function ColsTable({ cols }) {
  return (
    <table className="w-full text-sm mb-3"><thead><tr className="text-left text-n600 border-b border-hair"><th className="py-1">Champ</th><th>Statut</th><th>Description</th></tr></thead>
      <tbody>{cols.map(([n, s, d]) => (<tr key={n} className="border-b border-hair/50"><td className="py-1 adm-mono text-xs">{n}</td><td>{s === 'obligatoire' ? <b>obligatoire</b> : 'facultatif'}</td><td className="text-n600">{d}</td></tr>))}</tbody>
    </table>
  );
}
function ImportRubric() {
  const [preview, setPreview] = useState(null); // {data, validation}
  const fileRef = useRef(null);
  const onFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const validation = await adminApi.importConfig(data, false);
      setPreview({ data, validation });
    } catch (e) { toast.error('Fichier JSON illisible'); }
  };
  const doImport = async () => {
    try { const r = await adminApi.importConfig(preview.data, true);
      toast.success(`Configuration importée (${r.appellations} appellations)`); setPreview(null); if (fileRef.current) fileRef.current.value = ''; }
    catch (e) { toast.error(e.message); }
  };
  const v = preview?.validation;
  return (
    <div className="space-y-4">
      <PanelCard title="Configuration (JSON)" testId="rubric-import">
        <p className="text-sm text-n600 mb-3">Réimporte une configuration exportée. Le mot de passe SMTP n'est jamais importé : l'état passe à « à tester ».</p>
        <ColsTable cols={CONFIG_COLS} />
        <input ref={fileRef} type="file" accept="application/json,.json" onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-config-file" className="text-sm" />
        {v && (
          <div className="vd-card p-3 mt-3 bg-surface/40">
            {v.ok
              ? <p className="text-sm">Détecté : version {v.summary.version} · {v.summary.appellations} appellations · SMTP {v.summary.has_smtp ? 'présent' : 'absent'}</p>
              : <div><p className="adm-text-error text-sm mb-1">Fichier invalide :</p><ul className="text-xs text-red-700 list-disc ml-5">{v.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>}
            <div className="flex gap-2 mt-2">
              <Button onClick={() => { setPreview(null); if (fileRef.current) fileRef.current.value = ''; }}>Retirer</Button>
              <Button variant="primary" disabled={!v.ok} onClick={doImport} data-testid="import-config-confirm">Importer</Button>
            </div>
          </div>
        )}
      </PanelCard>
      <PanelCard title="Appellations (CSV)">
        <AppellationIndexImport />
      </PanelCard>
    </div>
  );
}

// ---------------- Super Admin (compte courant) ----------------
function SuperAdminRubric() {
  const [acc, setAcc] = useState(null);
  const [email, setEmail] = useState('');
  const [recovery, setRecovery] = useState('');
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.getAccount().then((a) => { setAcc(a); setEmail(a.email || ''); setRecovery(a.recovery_email || ''); });
  useEffect(() => { load(); }, []);
  if (!acc) return null;

  const emailChanged = email.trim().toLowerCase() !== (acc.email || '');
  const recoveryChanged = recovery.trim().toLowerCase() !== (acc.recovery_email || '');
  const pwValid = !pw || (pw.length >= 10 && pw === confirm);
  const needsCurrent = emailChanged || !!pw;
  const canSave = (emailChanged || recoveryChanged || !!pw) && pwValid && (!needsCurrent || current);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.updateAccount({ email, recovery_email: recovery,
        password: pw || undefined, current_password: current || undefined });
      toast.success('Compte super-admin mis à jour');
      setPw(''); setConfirm(''); setCurrent(''); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="rounded-md px-4 py-3 mb-4 text-sm font-medium"
        style={{ background: 'color-mix(in srgb, var(--adm-red) 12%, transparent)', color: 'var(--adm-red)' }}
        data-testid="superadmin-warning">
        Attention : c'est le compte Super Admin qui est en cours d'édition.
      </div>
      <PanelCard title="Identifiants" testId="rubric-superadmin">
        <Field label="Email (identifiant de connexion)"><input className="vd-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="sa-email" /></Field>
        <Field label="Email de secours" hint="Adresse alternative pour la récupération d'accès.">
          <input className="vd-input" type="email" value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="secours@exemple.fr" data-testid="sa-recovery" />
        </Field>
      </PanelCard>
      <PanelCard title="Changer le mot de passe" className="mt-4">
        <Field label="Nouveau mot de passe (laisser vide pour conserver)"><input className="vd-input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="sa-pw" /></Field>
        {pw ? <Field label="Confirmer"><input className="vd-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="sa-pw-confirm" /></Field> : null}
        {pw && !pwValid && <p className="adm-text-error text-xs mb-2">10 caractères minimum et les deux saisies identiques.</p>}
      </PanelCard>
      <PanelCard title="Confirmation" className="mt-4">
        {needsCurrent && <Field label="Mot de passe actuel (requis pour changer l'email ou le mot de passe)"><input className="vd-input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="sa-current" /></Field>}
        <Button variant="primary" disabled={!canSave || busy} onClick={save} data-testid="sa-save">{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
      </PanelCard>
    </div>
  );
}

// ---------------- Page ----------------
export default function Settings() {
  const [rubric, setRubric] = useState('instance');
  const [settings, setSettings] = useState(null);
  const [smtp, setSmtp] = useState(null);
  const [instance, setInstance] = useState(null);
  const [emails, setEmails] = useState({ h24: 0, d30: 0 });
  const [dirty, setDirty] = useState(false);

  const reload = () => Promise.all([adminApi.settings(), adminApi.getSmtp(), adminApi.instance(), adminApi.dashboard()])
    .then(([s, sm, inst, dash]) => { setSettings(s); setSmtp(sm); setInstance(inst); setEmails(dash.emails); });
  useEffect(() => { reload(); }, []);

  const go = (k) => { if (dirty && !window.confirm('Des modifications ne sont pas enregistrées. Changer de rubrique ?')) return; setDirty(false); setRubric(k); };
  if (!settings) return null;

  return (
    <div data-testid="admin-settings" className="flex gap-6">
      <nav className="w-[180px] shrink-0 self-start sticky top-4" data-testid="settings-rubrics">
        <h1 className="font-title text-2xl mb-4">Paramètres</h1>
        {RUBRICS.map((r) => (
          <button key={r.key} onClick={() => go(r.key)} data-testid={`rubric-${r.key}`}
            className={`adm-nav-item w-full ${rubric === r.key ? 'adm-nav-active' : ''}`}>
            <span>{r.label}</span>{dirty && rubric === r.key && <span className="adm-dot" />}
          </button>
        ))}
      </nav>
      <div className="flex-1 min-w-0 max-w-3xl">
        {rubric === 'instance' && <InstanceRubric settings={settings} instance={instance} setDirty={setDirty} />}
        {rubric === 'emails' && smtp && <EmailsRubric smtp={smtp} emails={emails} reload={reload} setDirty={setDirty} />}
        {rubric === 'logs' && <LogsRubric settings={settings} reload={reload} setDirty={setDirty} />}
        {rubric === 'export' && <ExportRubric settings={settings} reload={reload} setDirty={setDirty} />}
        {rubric === 'import' && <ImportRubric />}
        {rubric === 'ia' && <IASettings api={ADMIN_AI_API} />}
        {rubric === 'superadmin' && <SuperAdminRubric />}
      </div>
    </div>
  );
}
